import { ReactElement, Suspense } from 'react';
import { BrowserRouter, Route, Switch } from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';

import GTMTag from '@/components/googleTagManager';
import { CmLoading } from '@/components/uiParts';
import CamelKioskAccountCreatePage from '@/pages/camel-kiosk/accounts/create/CamelKioskAccountCreatePage';
import CamelKioskAccountsPage from '@/pages/camel-kiosk/accounts/index/CamelKioskAccountsPage';
import CamelKioskPasswordChangePage from '@/pages/camel-kiosk/accounts/password-change/CamelKioskPasswordChangePage';
import CamelKioskStorePage from '@/pages/camel-kiosk/stores/details/CamelKioskStorePage';
import CamelOrderCouponItemsNewPage from '@/pages/camel-order/coupons/create/CamelOrderCouponNewPage';
import CamelOrderCouponsPage from '@/pages/camel-order/coupons/index/CamelOrderCouponsPage';
import CamelOrderDeliveryPage from '@/pages/camel-order/delivery/CamelOrderDeliveryPage';
import CamelOrderSitesPage from '@/pages/camel-order/sites/CamelOrderSitesPage';
import TempHoursEditPage from '@/pages/camel-order/temp-hour/edit/TempHoursEditPage';
import TempHoursListPage from '@/pages/camel-order/temp-hour/list/TempHoursListPage';
import TempHoursNewPage from '@/pages/camel-order/temp-hour/new/TempHoursNewPage';
import DeliveryInHousePage from '@/pages/delivery/in-house/DeliveryInHousePage';
import MosPosCancelConfirmPage from '@/pages/externals/mos-pos-resend/detail/MosPosCancelConfirmPage';
import MosPosResendConfirmPage from '@/pages/externals/mos-pos-resend/detail/MosPosResendConfirmPage';
import MosPosResendListPage from '@/pages/externals/mos-pos-resend/index/MosPosResendListPage';
import PosConnectPage from '@/pages/externals/pos-connect/PosConnectPage';
import MosPosMenuMasterImportPage from '@/pages/externals/pos-menu-masters/import/MosPosMenuMasterImportPage';
import MosPosMenuMasterListPage from '@/pages/externals/pos-menu-masters/list/MosPosMenuMasterListPage';
import ForgotPasswordPage from '@/pages/forgot-password/ForgotPasswordPage';
import HomePage from '@/pages/home/HomePage';
import LinkItemsPage from '@/pages/link-items/LinkItemsPage';
import LinkItemsV2Page from '@/pages/link-items-v2/LinkItemsV2Page';
import LoginPage from '@/pages/login/LoginPage';
import MenuManagementAllocatedMenusPage from '@/pages/menu-managements/allocated-menus/MenuManagementAllocatedMenusPage';
import MenuManagementBusinessHoursEditPage from '@/pages/menu-managements/business-hours/edit/MenuManagementBusinessHoursEditPage';
import MenuManagementBusinessHoursPage from '@/pages/menu-managements/business-hours/list/MenuManagementBusinessHoursPage';
import MenuManagementBusinessHoursNewPage from '@/pages/menu-managements/business-hours/new/MenuManagementBusinessHoursNewPage';
import MenuManagementCategoriesEditPage from '@/pages/menu-managements/categories/edit/MenuManagementCategoriesEditPage';
import MenuManagementCategoriesPage from '@/pages/menu-managements/categories/list/MenuManagementCategoriesPage';
import MenuManagementCategoriesNewPage from '@/pages/menu-managements/categories/new/MenuManagementCategoriesNewPage';
import MenuManagementExecutionsPage from '@/pages/menu-managements/executions/list/MenuManagementExecutionsPage';
import MenuManagementExecutionsNewPage from '@/pages/menu-managements/executions/new/MenuManagementExecutionsNewPage';
import MenuManagementExecutionsShowPage from '@/pages/menu-managements/executions/show/MenuManagementExecutionsShowPage';
import MenuManagementItemsEditPage from '@/pages/menu-managements/items/edit/MenuManagementItemsEditPage';
import MenuManagementItemsPage from '@/pages/menu-managements/items/list/MenuManagementItemsPage';
import MenuManagementItemsNewPage from '@/pages/menu-managements/items/new/MenuManagementItemsNewPage';
import MenuManagementMenusEditPage from '@/pages/menu-managements/menus/edit/MenuManagementMenusEditPage';
import MenuManagementMenusPage from '@/pages/menu-managements/menus/list/MenuManagementMenusPage';
import MenuManagementMenusNewPage from '@/pages/menu-managements/menus/new/MenuManagementMenusNewPage';
import MenuManagementOptionGroupsEditPage from '@/pages/menu-managements/option-groups/edit/MenuManagementOptionGroupsEditPage';
import MenuManagementOptionGroupsPage from '@/pages/menu-managements/option-groups/list/MenuManagementOptionGroupsPage';
import MenuManagementOptionGroupsNewPage from '@/pages/menu-managements/option-groups/new/MenuManagementOptionGroupsNewPage';
import CSVExportPageV2 from '@/pages/ordersData/csv-v2/CSVExportPageV2';
import PageNotFoundPage from '@/pages/PageNotFoundPage';
import RegistrationPage from '@/pages/registration/RegistrationPage';
import ResendEmailRequestedPage from '@/pages/resend-email/requested/ResendEmailRequestedPage';
import ResendEmailPage from '@/pages/resend-email/ResendEmailPage';
import ResetPasswordRequestedPage from '@/pages/reset-password/requested/ResetPasswordRequestedPage';
import ResetPasswordPage from '@/pages/reset-password/ResetPasswordPage';
import SalesItemsPage from '@/pages/sales_report/items/SalesItemsPage';
import SalesRatePage from '@/pages/sales_report/rate/SalesRatePage';
import SalesPage from '@/pages/sales_report/SalesPage';
import MemberInvitePage from '@/pages/settings/member-management/invite/MemberInvitePage';
import MemberManagementPage from '@/pages/settings/member-management/MemberManagementPage';
import StoreManagementAuthStoresPage from '@/pages/store-management/auth-stores/StoreManagementAuthStoresPage';
import StoreManagementBrandsPage from '@/pages/store-management/brands/StoreManagementBrandsPage';
import StoreManagementDeliveryStoresStatusPage from '@/pages/store-management/delivery-stores-status-management/DeliveryStoresStatusManagementPage';
import StoreManagementMasterStoresPage from '@/pages/store-management/master-stores/StoreManagementMasterStoresPage';
import StoreManagementUberStoresActivatePage from '@/pages/store-management/uber-stores/activate/StoreManagementUberStoresActivatePage';

export const routeParrentPath = {
  ordersData: '/orders-data',
  menuManagements: '/menu-managements',
  camelOrder: '/camel-order',
  camelKiosk: '/camel-kiosk',
  delivery: '/delivery',
  storeManagements: '/store-managements',
  externals: '/externals',
  settings: '/settings',
  salesReport: '/sales-report',
} as const;

const {
  ordersData,
  menuManagements,
  storeManagements,
  externals,
  settings,
  camelOrder,
  camelKiosk,
  delivery,
  salesReport,
} = routeParrentPath;

export const routes = {
  /** 認証周り  **/
  Login: { title: 'ログイン', path: '/login', component: LoginPage },
  ForgotPassword: {
    title: 'パスワード再設定',
    path: '/forgot-password',
    component: ForgotPasswordPage,
  },
  Registration: {
    title: 'アカウント本登録',
    path: '/registration',
    component: RegistrationPage,
  },
  ResendEmail: {
    title: 'アカウント本登録メール再送',
    path: '/resend-email',
    component: ResendEmailPage,
  },
  ResendEmailRequested: {
    title: 'アカウント本登録メール 再送信完了',
    path: '/resend-email/requested',
    component: ResendEmailRequestedPage,
  },
  ResetPassword: {
    title: 'パスワード再設定',
    path: '/reset-password',
    component: ResetPasswordPage,
  },
  ResetPasswordRequested: {
    title: 'パスワード再設定 メール送信完了',
    path: '/reset-password/requested',
    component: ResetPasswordRequestedPage,
  },

  Home: { title: 'ホーム', path: '/', component: HomePage },

  // 売上レポート
  SalesReport: {
    title: '売上レポート',
    path: `${salesReport}/sales`,
    component: SalesPage,
  },
  ItemsReport: {
    title: '商品レポート',
    path: `${salesReport}/items`,
    component: SalesItemsPage,
  },
  SalesRates: {
    title: '売上比率',
    path: `${salesReport}/rates`,
    component: SalesRatePage,
  },

  /** 注文データ  **/
  OrdersDataV2: {
    title: 'CSVダウンロードV2',
    path: `${ordersData}/csv-v2`,
    component: CSVExportPageV2,
  },

  /** 一括品切れ商品紐付け  **/
  LinkItems: {
    title: '一括品切れ商品紐付け',
    path: '/link-items',
    component: LinkItemsPage,
  },
  LinkItemsV2: {
    title: '一括品切れ設定V2',
    path: '/link-items-v2',
    component: LinkItemsV2Page,
  },

  /** メニュー管理  **/
  menuManagementItems: {
    title: '商品',
    path: `${menuManagements}/items`,
    component: MenuManagementItemsPage,
  },
  menuManagementItemsNew: {
    title: '商品登録',
    path: `${menuManagements}/items/new`,
    component: MenuManagementItemsNewPage,
  },
  menuManagementItemsEdit: {
    title: '商品編集',
    path: `${menuManagements}/items/:id/edit`,
    component: MenuManagementItemsEditPage,
  },

  menuManagementCategories: {
    title: 'カテゴリ',
    path: `${menuManagements}/categories`,
    component: MenuManagementCategoriesPage,
  },
  menuManagementCategoriesNew: {
    title: 'カテゴリ登録',
    path: `${menuManagements}/categories/new`,
    component: MenuManagementCategoriesNewPage,
  },
  menuManagementCategoriesEdit: {
    title: 'カテゴリ編集',
    path: `${menuManagements}/categories/:id/edit`,
    component: MenuManagementCategoriesEditPage,
  },

  menuManagementOptionGroups: {
    title: 'オプショングループ',
    path: `${menuManagements}/option-groups`,
    component: MenuManagementOptionGroupsPage,
  },
  menuManagementOptionGroupsNew: {
    title: 'オプショングループ登録',
    path: `${menuManagements}/option-groups/new`,
    component: MenuManagementOptionGroupsNewPage,
  },
  menuManagementOptionGroupsEdit: {
    title: 'オプショングループ編集',
    path: `${menuManagements}/option-groups/:id/edit`,
    component: MenuManagementOptionGroupsEditPage,
  },

  menuManagementMenus: {
    title: 'メニュー',
    path: `${menuManagements}/menus`,
    component: MenuManagementMenusPage,
  },
  menuManagementMenusNew: {
    title: 'メニュー登録',
    path: `${menuManagements}/menus/new`,
    component: MenuManagementMenusNewPage,
  },
  menuManagementMenusEdit: {
    title: 'メニュー編集',
    path: `${menuManagements}/menus/:id/edit`,
    component: MenuManagementMenusEditPage,
  },

  menuManagementBusinessHours: {
    title: 'メニュー提供時間',
    path: `${menuManagements}/business-hours`,
    component: MenuManagementBusinessHoursPage,
  },
  menuManagementBusinessHoursNewPage: {
    title: 'メニュー提供時間登録',
    path: `${menuManagements}/business-hours/new`,
    component: MenuManagementBusinessHoursNewPage,
  },
  menuManagementBusinessHoursEdit: {
    title: 'メニュー提供時間編集',
    path: `${menuManagements}/business-hours/:id/edit`,
    component: MenuManagementBusinessHoursEditPage,
  },

  menuManagementAllocations: {
    title: '店舗メニュー割り当て',
    path: `${menuManagements}/allocated-menus`,
    component: MenuManagementAllocatedMenusPage,
  },

  menuManagementExecutions: {
    title: 'メニュー配信',
    path: `${menuManagements}/executions`,
    component: MenuManagementExecutionsPage,
  },
  menuManagementExecutionsNew: {
    title: 'メニュー配信登録',
    path: `${menuManagements}/executions/new`,
    component: MenuManagementExecutionsNewPage,
  },
  menuManagementExecutionsEdit: {
    title: 'メニュー配信詳細',
    path: `${menuManagements}/executions/:id`,
    component: MenuManagementExecutionsShowPage,
  },
  /** Camel Order **/
  CamelOrderSites: {
    title: 'Camel Orderサイト設定',
    path: `${camelOrder}/sites`,
    component: CamelOrderSitesPage,
  },
  tempHourNew: {
    title: '休業日・臨時営業時間の追加',
    path: `${camelOrder}/temp-hours/new`,
    component: TempHoursNewPage,
  },
  tempHourList: {
    title: '休業日・臨時営業時間設定',
    path: `${camelOrder}/temp-hours`,
    component: TempHoursListPage,
  },
  tempHourEdit: {
    title: '休業日・臨時営業時間の編集',
    path: `${camelOrder}/temp-hours/:id`,
    component: TempHoursEditPage,
  },
  // TODO: 自社配達機能リリース時には、配達関連設定を削除する
  camelOrderDelivery: {
    title: '配達関連設定',
    path: `${camelOrder}/delivery`,
    component: CamelOrderDeliveryPage,
  },
  camelOrderCoupons: {
    title: 'クーポン設定',
    path: `${camelOrder}/coupons`,
    component: CamelOrderCouponsPage,
  },
  camelOrderCouponsNew: {
    title: 'クーポン登録',
    path: `${camelOrder}/coupons/:site_id/new`,
    component: CamelOrderCouponItemsNewPage,
  },

  /** Camel Kiosk **/
  CamelKioskAccounts: {
    title: 'Kioskアカウント管理',
    path: `${camelKiosk}/accounts`,
    component: CamelKioskAccountsPage,
  },
  CamelKioskAccountCreate: {
    title: 'Kioskアカウント新規作成',
    path: `${camelKiosk}/accounts/:id/create`,
    component: CamelKioskAccountCreatePage,
  },
  CamelKioskPasswordChange: {
    title: 'Kioskアカウントパスワード変更',
    path: `${camelKiosk}/accounts/:id/password-change`,
    component: CamelKioskPasswordChangePage,
  },
  CamelKioskStore: {
    title: 'Kiosk基本設定',
    path: `${camelKiosk}/stores/:id`,
    component: CamelKioskStorePage,
  },

  /** 配達設定 **/
  deliveryOutsource: {
    title: '配達代行設定',
    path: `${delivery}/outsource`,
    component: CamelOrderDeliveryPage,
  },
  deliveryInHouse: {
    title: '自社配達設定',
    path: `${delivery}/in-house`,
    component: DeliveryInHousePage,
  },

  /** 店舗管理  **/
  storeManagementAuthStores: {
    title: '拠点一覧',
    path: `${storeManagements}/auth-stores`,
    component: StoreManagementAuthStoresPage,
  },
  storeManagementBrands: {
    title: 'ブランド一覧',
    path: `${storeManagements}/brands`,
    component: StoreManagementBrandsPage,
  },
  storeManagementMasterStores: {
    title: '店舗一覧',
    path: `${storeManagements}/master-stores`,
    component: StoreManagementMasterStoresPage,
  },
  storeManagementUberStoresActivate: {
    title: 'Uber Eats店舗認証',
    path: `${storeManagements}/uber-stores/activate`,
    component: StoreManagementUberStoresActivatePage,
  },
  deliveryStoresStatusManagement: {
    title: '店舗受付ステータス管理',
    path: `${storeManagements}/delivery-stores-status`,
    component: StoreManagementDeliveryStoresStatusPage,
  },

  // 外部連携設定
  PosConnect: {
    title: 'POS連携',
    path: `${externals}/pos-connect`,
    component: PosConnectPage,
  },
  MosPosResendList: {
    title: 'POS連携再送信',
    path: `${externals}/mos-pos-resend`,
    component: MosPosResendListPage,
  },
  MosPosResendConfirm: {
    title: 'POS連携再送信 - 再送信確認',
    path: `${externals}/mos-pos-resend/:orderId/resend`,
    component: MosPosResendConfirmPage,
  },
  MosPosCancelConfirm: {
    title: 'POS連携再送信 - 取消確認',
    path: `${externals}/mos-pos-resend/:orderId/cancel`,
    component: MosPosCancelConfirmPage,
  },
  // より具体的な /import を先に置き、一覧のパスに吸われないようにします。
  MosPosMenuMasterImport: {
    title: 'CSVインポート',
    path: `${externals}/pos-menu-masters/import`,
    component: MosPosMenuMasterImportPage,
  },
  MosPosMenuMasterList: {
    title: 'POSメニューマスタ',
    path: `${externals}/pos-menu-masters`,
    component: MosPosMenuMasterListPage,
  },

  /** 設定  **/
  SettingsMemberManagement: {
    title: 'メンバー管理',
    path: `${settings}/member-managements`,
    component: MemberManagementPage,
  },
  SettingsMemberManagementInvite: {
    title: 'メンバー招待',
    path: `${settings}/member-managements/invite`,
    component: MemberInvitePage,
  },

  /* ページが見つからない場合 */
  motFound: {
    title: '',
    path: '*',
    component: PageNotFoundPage,
  },
} as const;

export type Routes = typeof routes[keyof typeof routes];

// 認証関連のページパス
export const authPagePaths: string[] = [
  routes.Login.path,
  routes.ForgotPassword.path,
  routes.Registration.path,
  routes.ResendEmail.path,
  routes.ResendEmailRequested.path,
  routes.ResetPassword.path,
  routes.ResetPasswordRequested.path,
];

export const Router = (): ReactElement => (
  <BrowserRouter>
    <GTMTag />
    <QueryParamProvider ReactRouterRoute={Route}>
      <Suspense fallback={<CmLoading />}>
        <Switch>
          {Object.values(routes).map((page, index) => (
            <Route
              key={page.path + index}
              exact
              path={page.path}
              component={page.component}
            />
          ))}
        </Switch>
      </Suspense>
    </QueryParamProvider>
  </BrowserRouter>
);
